import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import {
	Confirm,
	Input,
	Modal,
	SpinnerOverlay,
	SubmissionButtons } from '@components/common';

import { useMutateCheckAssetSubTypeName } from '@hooks/assets/assetTypes/useMutateCheckAssetSubTypeName';
import { useMutateCreateAssetSubType } from '@hooks/assets/assetTypes/useMutateCreateAssetSubType';
import { useMutateUpdateAssetSubType } from '@hooks/assets/assetTypes/useMutateUpdateAssetSubType';

import { EValidationMode } from '@enums/validationMode';

import {
	assetSubTypeInputLabel,
	assetSubTypeInputName,
	assetSubTypeInputPlaceholder,
} from '../constants';

import { assetSubTypeSchema } from './schema';

import styles from './AssetSubTypeForm.module.scss';

interface IAssetSubTypeFormProps {
	assetSubTypeName?: string;
	assetTypeName?: string;
	id?: string;
	setIsDirtyFormModal: (isDirty: boolean) => void;
	onClose: () => void;
	closeFormModal: () => void;
}

interface IFormValues {
	assetSubTypeName: string;
}

const AssetSubTypeForm = ({
	assetSubTypeName,
	assetTypeName,
	id,
	onClose,
	setIsDirtyFormModal,
	closeFormModal,
}: IAssetSubTypeFormProps) => {
	const [isUpdateConfirm, setIsUpdateConfirm] = useState(false);

	const openUpdateConfirm = useCallback(() => setIsUpdateConfirm(true), []);
	const closeUpdateConfirm = useCallback(() => setIsUpdateConfirm(false), []);

	const { handleCheckAssetSubTypeName } = useMutateCheckAssetSubTypeName();

	const {
		formState: { errors, isDirty },
		register,
		setValue,
		handleSubmit,
		watch,
	} = useForm<IFormValues>({
		mode: EValidationMode.onSubmit,
		resolver: yupResolver(assetSubTypeSchema(handleCheckAssetSubTypeName)),
		defaultValues: {
			assetSubTypeName: assetSubTypeName || '',
		},
	});

	const inputError = errors?.assetSubTypeName?.message;

	useEffect(() => setIsDirtyFormModal(isDirty), [isDirty, setIsDirtyFormModal]);

	const inputValue = watch(assetSubTypeInputName);
	const isSameAssetSubTypeName = assetSubTypeName === inputValue;

	const {
		handleCreateAssetSubType,
		isLoadingCreateAssetSubType,
	} = useMutateCreateAssetSubType();

	const {
		handleUpdateAssetSubType,
		isLoadingUpdateAssetSubType,
	} = useMutateUpdateAssetSubType();

	const updateAssetTypeHandler = useCallback(() => {
		if (!assetTypeName || !id) return;

		handleUpdateAssetSubType({
			assetSubTypeName: inputValue,
			assetTypeName,
			id,
		});

		closeFormModal();
		closeUpdateConfirm();
	}, [
		inputValue,
		id,
		closeFormModal,
		closeUpdateConfirm,
		handleUpdateAssetSubType,
		assetTypeName,
	]);

	const onSubmit = useCallback(() => {
		if (!assetTypeName) return;

		try {
			if (assetSubTypeName && id) {
				openUpdateConfirm();
			} else {
				handleCreateAssetSubType({
					assetSubTypeName: inputValue,
					assetTypeName,
				});

				closeFormModal();
			}
		} catch (_e) {
			return;
		}

	}, [
		assetSubTypeName,
		assetTypeName,
		id,
		inputValue,
		closeFormModal,
		handleCreateAssetSubType,
		openUpdateConfirm,
	]);

	const isLoading = isLoadingCreateAssetSubType || isLoadingUpdateAssetSubType;

	return (
		<div className={styles.container}>
			{isLoading && <SpinnerOverlay />}
			<Modal
				isOpen={isUpdateConfirm}
				onClose={closeUpdateConfirm}
			>
				<Confirm
					isLoading={isLoadingUpdateAssetSubType}
					onClose={closeUpdateConfirm}
					onSubmit={updateAssetTypeHandler}
					question='Are you sure you want to update the asset subtype?'
				>
					Changes will affect organisations using this asset subtype.
				</Confirm>
			</Modal>
			<div className={styles.textWrapper}>
				<span className={styles.boldText}>Asset Type: </span>
				<span>{assetTypeName}</span>
			</div>
			<Input
				errorMessage={inputError}
				label={assetSubTypeInputLabel}
				name={assetSubTypeInputName}
				placeholder={assetSubTypeInputPlaceholder}
				register={register}
				required
				setValue={setValue}
				value={inputValue}
			/>
			<SubmissionButtons
				disabledPositive={!inputValue || isSameAssetSubTypeName}
				negativeHandler={onClose}
				positiveHandler={handleSubmit(onSubmit)}
				positiveLabel='Save'
			/>
		</div>
	);
};

export default AssetSubTypeForm;
